// crewbit-admin/src/lib/invoiceShare.ts
//
// Phase B.3.2 — Invoice share/copy-link utilities (split from invoiceMutations.ts
// per Golden Rule #14 file-size cap, Session 125). Build the hosted invoice URL
// (https://crewbit.app/{slug}/pay/{token}, hostedPageUrl) and surface it via the Web Share API
// (`share`, with clipboard fallback when `navigator.share` is unavailable) or
// paste it to the clipboard (`copyLink`). Token shape mirrors mobile + the
// BEFORE INSERT trigger `set_invoice_share_token`: 16 random bytes hex-encoded.

import { supabase } from './supabase';
import { hostedPageUrl } from './hostedPageUrl';

export interface ShareInvoiceArgs {
  invoiceId: string;
  /** businesses.slug — the hosted page is keyed by it (useAuthStore.business.slug). */
  slug: string;
}

function generateShareToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function getOrGenerateShareToken(
  invoiceId: string,
): Promise<{ ok: true; token: string } | { ok: false; error: string }> {
  const { data: invRow, error: readErr } = await supabase
    .from('invoices')
    .select('share_token')
    .eq('id', invoiceId)
    .maybeSingle();
  if (readErr) return { ok: false, error: readErr.message };
  if (invRow?.share_token) return { ok: true, token: invRow.share_token };

  const newToken = generateShareToken();
  const { error: updateErr } = await supabase
    .from('invoices')
    .update({ share_token: newToken })
    .eq('id', invoiceId);
  if (updateErr) return { ok: false, error: updateErr.message };
  return { ok: true, token: newToken };
}

export async function share(args: ShareInvoiceArgs): Promise<{ success: boolean; error?: string }> {
  const tokenResult = await getOrGenerateShareToken(args.invoiceId);
  if (!tokenResult.ok) return { success: false, error: tokenResult.error };

  const url = hostedPageUrl(args.slug, 'pay', tokenResult.token);
  if (typeof navigator !== 'undefined' && 'share' in navigator) {
    try {
      await navigator.share({ url });
      return { success: true };
    } catch (e) {
      // User-cancelled share is not a real error in Web Share API; fall
      // through to clipboard fallback rather than surfacing it.
      if (e instanceof Error && e.name === 'AbortError') {
        return { success: false, error: 'aborted' };
      }
      // Other errors fall through to clipboard fallback below.
    }
  }
  return copyLink(args);
}

export async function copyLink(args: ShareInvoiceArgs): Promise<{ success: boolean; error?: string }> {
  const tokenResult = await getOrGenerateShareToken(args.invoiceId);
  if (!tokenResult.ok) return { success: false, error: tokenResult.error };

  const url = hostedPageUrl(args.slug, 'pay', tokenResult.token);
  try {
    await navigator.clipboard.writeText(url);
    return { success: true };
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'clipboard_failed' };
  }
}
